import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import HeaderTabs from '../Header/HeaderTabs';
import { navigationConfig, bottomNavigationConfig } from '../../config/navigation';
import { getSidebarConfig } from '../../themes/responsive';

// Shared horizontal padding for page alignment (DRY)
export const pageHorizontalPadding = { xs: 3, sm: 3, md: 6};

const Layout = ({ 
  children, 
  activePage, 
  onPageChange, 
  headerTabs = [], 
  activeHeaderTab = 0, 
  onHeaderTabChange = () => {},
  rightSidebar = null,
  overlayMode = false,
  // Overrides the sidebar width; meant for overlayMode (the docked padding offset stays the default)
  sidebarWidth = null
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Below `md` (900px) the permanent sidebar would crowd the viewport, so we
  // switch to a temporary drawer toggled by a hamburger in the header.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Close the mobile drawer whenever we grow back to a desktop width.
  React.useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  // Get the page title from navigation config with translation support
  const getPageTitle = () => {
    const page = navigationConfig.find(item => item.id === activePage) ||
                 bottomNavigationConfig.find(item => item.id === activePage);
    if (!page) return activePage;
    
    // Use titleKey for translation if available, otherwise use title directly
    return page.titleKey ? t(page.titleKey) : page.title;
  };

  // Get responsive sidebar configuration
  const sidebarConfig = getSidebarConfig();

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Sidebar
        activePage={activePage}
        onPageChange={onPageChange}
        activeTab={activeHeaderTab}
        onTabChange={onHeaderTabChange}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      
      {/* Main content area */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default',
          overflow: 'auto',
          transition: 'margin-left 400ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        }}
      >
        {/* Header */}
        <Header
          title={getPageTitle()}
          showMenuButton={isMobile}
          onMenuClick={() => setMobileOpen(true)}
        />
        <HeaderTabs 
          tabs={headerTabs}
          activeTab={activeHeaderTab}
          onTabChange={onHeaderTabChange}
          showTabs={headerTabs.length > 0}
        />
        
        {/* Content Area with optional sidebar */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              minWidth: 0,
              ...(rightSidebar && !overlayMode && !isMobile && { paddingRight: sidebarConfig.paddingOffset })
            }}
            onClick={(e) => {
              // Close sidebar when clicking on main content area (only for non-overlay sidebars)
              if (rightSidebar && !overlayMode && e.target === e.currentTarget) {
                window.currentPageSidebar = null;
                window.dispatchEvent(new Event('sidebarUpdate'));
              }
            }}
          >
            {children}
          </Box>
          
          {/* Right Sidebar */}
          {rightSidebar && (
            <>
              {/* Overlay for click-outside functionality */}
              {(overlayMode || isMobile) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    zIndex: 15,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    // Close sidebar
                    window.currentPageSidebar = null;
                    window.dispatchEvent(new Event('sidebarUpdate'));
                  }}
                />
              )}

              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: isMobile ? '100%' : (sidebarWidth || sidebarConfig.width),
                maxWidth: '100%',
                height: '100%',
                backgroundColor: 'background.paper',
                borderLeft: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
                zIndex: (overlayMode || isMobile) ? 20 : 10,
                p: 0,
                ...((overlayMode || isMobile) && {
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                  transform: 'translateZ(0)', // Force hardware acceleration
                })
              }}>
                {rightSidebar}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;